import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BookOpen, Sparkles, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PlaybookGallery({ open, onOpenChange, onInstalled }) {
  const [items, setItems] = useState([]);
  const [offer, setOffer] = useState("");
  const [busy, setBusy] = useState(null);
  useEffect(() => { if (open) api.get("/playbooks").then((r) => setItems(r.data)); }, [open]);

  const install = async (p) => {
    setBusy(p.key);
    try {
      const { data } = await api.post(`/playbooks/${p.key}/install`, { offer });
      toast.success(`Installed "${p.name}" — review steps, then enroll contacts`);
      onInstalled(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="playbook-gallery">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="w-4 h-4" />Done-for-you playbooks</DialogTitle><DialogDescription>Proven multi-step sequences by industry. Install one, tweak the copy, enroll contacts. Steps use tokens and (where marked) Claude personalization.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><div className="text-[10px] uppercase tracking-[0.1em] font-medium text-muted-foreground">What you sell (optional — used by AI steps)</div><Input data-testid="playbook-offer" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="e.g. Done-for-you outbound for B2B agencies" /></div>
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((p) => (
            <div key={p.key} className="border border-border rounded-md bg-card p-4 flex flex-col gap-2 hover:border-primary/40 transition-colors duration-150" data-testid={`playbook-${p.key}`}>
              <div className="flex items-start justify-between gap-2"><div className="text-sm font-medium leading-tight">{p.name}</div><span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground border border-border px-1.5 rounded shrink-0">{p.industry}</span></div>
              <div className="text-xs text-muted-foreground"><span className="text-foreground/80">Audience:</span> {p.audience}</div>
              <div className="text-xs text-muted-foreground"><span className="text-foreground/80">Goal:</span> {p.goal}</div>
              <div className="text-[11px] mono text-muted-foreground flex items-center gap-2 flex-wrap"><Sparkles className="w-3 h-3 text-primary" />{p.steps_summary}{p.require_approval && <span className="inline-flex items-center gap-1 text-amber-500"><ShieldCheck className="w-3 h-3" />approval on</span>}</div>
              <Button size="sm" variant={p.installed ? "outline" : "default"} className="mt-auto" disabled={busy === p.key} onClick={() => install(p)} data-testid={`playbook-install-${p.key}`}>{p.installed ? <><Check className="w-3.5 h-3.5 mr-1.5" />Install again</> : busy === p.key ? "Installing..." : "Install playbook"}</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
